import { type AuthProviders, GOOGLE_LOGIN_CALLBACK_PATH } from "@backchannel/tax-contracts";
import {
  createCodeChallenge,
  createCodeVerifier,
  randomUrlSafeString,
  saveTransaction,
} from "./pkce";

export type GoogleProvider = NonNullable<AuthProviders["google"]>;

const GOOGLE_AUTHORIZATION_ENDPOINT = "https://accounts.google.com/o/oauth2/v2/auth";

/** This page's own callback, the only redirect URI it may send to Google. */
export function ownGoogleCallback(): string | null {
  if (typeof window === "undefined") return null;
  return `${window.location.origin}${GOOGLE_LOGIN_CALLBACK_PATH}`;
}

/**
 * Google returns to a registered callback. Offer the button only when this
 * origin's callback is one the API accepts (tax.backchannel.com.br, or an
 * allow-listed local dev server): the PKCE transaction lives in this tab's
 * sessionStorage and cannot cross origins.
 */
export function isGoogleCallbackOrigin(provider: GoogleProvider): boolean {
  const own = ownGoogleCallback();
  if (!own) return false;
  return (provider.redirectUris ?? [provider.redirectUri]).includes(own);
}

export function buildGoogleAuthorizationUrl(input: {
  provider: GoogleProvider;
  redirectUri: string;
  state: string;
  nonce: string;
  codeChallenge: string;
  locale?: string;
}): string {
  const url = new URL(GOOGLE_AUTHORIZATION_ENDPOINT);
  url.search = new URLSearchParams({
    client_id: input.provider.clientId,
    redirect_uri: input.redirectUri,
    response_type: "code",
    scope: "openid email profile",
    code_challenge: input.codeChallenge,
    code_challenge_method: "S256",
    state: input.state,
    nonce: input.nonce,
    prompt: "select_account",
    ...(input.provider.hostedDomain ? { hd: input.provider.hostedDomain } : {}),
    ...(input.locale ? { hl: input.locale } : {}),
  }).toString();
  return url.toString();
}

/**
 * Start the Authorization Code + PKCE redirect. The verifier, state and nonce
 * stay in sessionStorage; the Tax API exchanges the code and only then issues
 * the normal first-party session.
 */
export async function beginGoogleSignIn(
  provider: GoogleProvider,
  options: { locale?: string; returnTo?: string } = {},
): Promise<void> {
  const codeVerifier = createCodeVerifier();
  const state = randomUrlSafeString(32);
  const nonce = randomUrlSafeString(32);
  const codeChallenge = await createCodeChallenge(codeVerifier);
  const redirectUri = ownGoogleCallback() ?? provider.redirectUri;
  saveTransaction({
    state,
    nonce,
    codeVerifier,
    returnTo: options.returnTo ?? "/",
    createdAt: Date.now(),
    provider: "google",
    redirectUri,
  });
  window.location.assign(
    buildGoogleAuthorizationUrl({
      provider,
      redirectUri,
      state,
      nonce,
      codeChallenge,
      locale: options.locale,
    }),
  );
}

const GOOGLE_NOTICE_KEY = "tax.auth.googleNotice";

/**
 * A Google sign-in that failed on the callback (wrong Workspace domain,
 * cancelled consent, provider down) hands its reason code back to the
 * sign-in card, which shows it inline next to the retry button. Only the
 * short reason code is stored — never the authorization code or tokens.
 */
export function storeGoogleSignInNotice(code: string): void {
  try {
    window.sessionStorage.setItem(GOOGLE_NOTICE_KEY, code);
  } catch {
    // Storage blocked: the card simply opens without the notice.
  }
}

export function takeGoogleSignInNotice(): string | null {
  try {
    const code = window.sessionStorage.getItem(GOOGLE_NOTICE_KEY);
    if (code) window.sessionStorage.removeItem(GOOGLE_NOTICE_KEY);
    return code;
  } catch {
    return null;
  }
}
